import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useBookings, attendeeOutcome, type Attendee } from '../api/use-bookings.js';
import { firstName } from '../api/first-name.js';
import { hasStarted } from '../api/has-started.js';
import { NoteRead } from '../components/note-read.js';
import { FamilyVideos } from '../components/family-videos.js';
import { WaitNote } from '../components/wait-note.js';

/**
 * One child's lesson, as the family reads it.
 *
 * WHAT IS HERE DEPENDS ON WHERE THE LESSON IS (mockups of 11 October):
 *   - Booked, still ahead: the facts, the practice plan once the coach has
 *     written it (ruled 13 October — it is what the lesson will do), and
 *     Cancel at the foot.
 *   - Taken place and closed: the facts, then the coach's note — only once he
 *     has closed it (ruled 12 October), so a draft is never read — and the
 *     plan.
 *   - Cancelled: who cancelled and when, and the coach's message if he sent
 *     one.
 *   - Any lesson with videos: the coach's and the child's, with Remove on
 *     the child's (step 7b, canvas row 4, approved 17 October).
 *
 * CANCEL LIVES HERE, AND ONLY HERE. It moved from the booking's page so that
 * there is one place to cancel a child, and it goes once the hour has started
 * (ruled 12 October).
 *
 * It reads the same bookings list as every other lesson screen; there is
 * nothing here that list does not carry.
 */
export function AthleteLessonRoute() {
  const { bookingId, lessonId } = useParams();
  const [params] = useSearchParams();
  const fromPerson = params.get('from') === 'person';
  const { state } = useBookings();

  if (state.status === 'loading') return <WaitNote className="empty">Checking…</WaitNote>;
  if (state.status === 'failed') return <p className="empty">{state.message}</p>;

  const lesson = state.lessons.find((candidate) => candidate.booking_id === bookingId);
  const attendee = lesson?.attendees.find((candidate) => candidate.lesson_id === lessonId);

  if (!lesson || !attendee) {
    return (
      <section className="panel">
        <h2>Not one of yours</h2>
        <p className="empty">No lesson with that link is on your account.</p>
        <Link className="primary" to="/lessons">
          Back to your lessons
        </Link>
      </section>
    );
  }

  const outcome = attendeeOutcome(lesson, attendee);
  const back = fromPerson
    ? { to: `/account/people/${attendee.client_id}`, label: `Back to ${firstName(attendee.name)}` }
    : lesson.attendees.length > 1
      ? { to: `/lessons/${lesson.booking_id}`, label: 'Back to the lesson' }
      : { to: '/lessons', label: 'Back to your lessons' };

  const canCancel =
    outcome === 'Booked' && !attendee.lesson_closed && !hasStarted(lesson.slot_start);

  return (
    <>
      <div className="page-head">
        <Link className="back" to={back.to} aria-label={back.label}>
          ‹
        </Link>
        <div>
          <h2>{when(lesson.slot_start)}</h2>
          <span className="detail">{outcome}</span>
        </div>
      </div>

      <Fact label="Athlete" value={attendee.name} />
      <Fact label="Coach" value={lesson.coach_name || '—'} />
      <Fact label="Lesson" value={lesson.product_name ?? lesson.service_name} />
      <Fact label="Where" value={lesson.facility_name} />
      {attendee.swing_count !== null ? <Fact label="Swings" value={String(attendee.swing_count)} /> : null}

      <Cancelled attendee={attendee} coachName={lesson.coach_name} />

      {/* Plan first: it is what the lesson will do, the note what it did
          (ordered 13 October). */}
      {attendee.practice_plan ? (
        <>
          <span className="section-label">Practice plan</span>
          <NoteRead html={attendee.practice_plan} />
        </>
      ) : null}

      {attendee.notes ? (
        <>
          <span className="section-label">Note</span>
          <NoteRead html={attendee.notes} />
        </>
      ) : null}

      {attendee.lesson_id ? (
        <FamilyVideos lessonId={attendee.lesson_id} childFirst={firstName(attendee.name)} coachName={lesson.coach_name} />
      ) : null}

      {canCancel ? (
        <Link
          className="warn-outline"
          to={`/lessons/${lesson.booking_id}/cancel/${attendee.lesson_id}${fromPerson ? '?from=person' : ''}`}
        >
          Cancel this lesson
        </Link>
      ) : null}
    </>
  );
}

/** Who cancelled this child's lesson, when, and what the coach said. */
function Cancelled({ attendee, coachName }: { attendee: Attendee; coachName: string }) {
  const cancellation = attendee.cancellation;
  if (!cancellation) return null;
  const coach = coachName ? `Coach ${firstName(coachName)}` : 'the coach';
  const by = cancellation.by === 'coach' ? coach : 'you';
  const on = new Date(cancellation.at).toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });

  return (
    <>
      <Fact label="Cancelled" value={`By ${by} · ${on}`} />
      {cancellation.message ? (
        <>
          <span className="section-label">From {coach}</span>
          <div className="note-read">{cancellation.message}</div>
        </>
      ) : null}
    </>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="fact">
      <span className="label">{label}</span>
      <span className="value">{value}</span>
    </div>
  );
}

function when(iso: string): string {
  return new Date(iso).toLocaleString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  });
}
